import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, BoltIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import JSONEditor from './JSONEditor';
import AsyncSelect from 'react-select/async';
import { useAuth } from '../contexts/AuthContext';
import I18nField from './I18nField';
import ThumbnailUpload from './ThumbnailUpload';
import { createI18nObject } from '../utils/i18n';

const NOZZLE_SIZES = ['0_2', '0_4', '0_6'];
const LAYER_HEIGHTS = ['0.24', '0.20', '0.12', '0.08'];
const nozzleToDot = (key) => key.replace('_', '.');

export default function SlicerProcessForm({ process, onSave, onCancel }) {
  const { user } = useAuth();
  const supportedLanguages = user?.supportedLanguages || ['pt'];

  const [slicerMachines, setSlicerMachines] = useState([]);
  const [machineModels, setMachineModels] = useState([]);
  const [formData, setFormData] = useState({
    quality: {
      nozzle: '0_4',
      layerHeight: '0.20',
      multiplier: 1.0
    },
    name: createI18nObject(supportedLanguages),
    description: createI18nObject(supportedLanguages),
    thumbnail: null,
    override: '{}',
    settings: [],
    notes: '',
    showInSite: true,
    isActive: true
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [error, setError] = useState('');
  const [systemSettingsOptions, setSystemSettingsOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState({});
  const [expandedOverrides, setExpandedOverrides] = useState({});

  useEffect(() => {
    fetchSlicerMachines();
    fetchMachineModels();
    // Fetch unfiltered defaults
    api.get('/auth/slicer-settings/base', { params: { type: 'process' } })
      .then(res => {
        const opts = (res.data?.data || []).map(item => ({ value: item._id, label: item.name }));
        setSystemSettingsOptions(opts);
      })
      .catch(err => console.error('Failed to fetch system settings:', err));
  }, []);

  useEffect(() => {
    if (process) {
      setFormData({
        quality: {
          nozzle: process.quality?.nozzle || '0_4',
          layerHeight: process.quality?.layerHeight || '0.20',
          multiplier: process.quality?.multiplier || 1.0
        },
        name: process.name || createI18nObject(supportedLanguages),
        description: process.description || createI18nObject(supportedLanguages),
        thumbnail: process.thumbnail || null,
        override: process.override || '{}',
        settings: process.settings || [],
        notes: process.notes || '',
        showInSite: process.showInSite ?? true,
        isActive: process.isActive ?? true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init form when prop changes
  }, [process]);

  const fetchSlicerMachines = async () => {
    try {
      const response = await api.get('/auth/slicer-settings/machines');
      setSlicerMachines(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch slicer machines:', err);
    }
  };

  const fetchMachineModels = async () => {
    try {
      const res = await api.get('/auth/machine-models/by-vendor');
      const groups = res.data?.data || res.data || [];
      setMachineModels(groups.flatMap(g => g.models || []));
    } catch (err) {
      console.error('Failed to fetch machine models:', err);
    }
  };

  const getMachineModelName = (modelId) => {
    return machineModels.find(m => m._id === modelId)?.name || null;
  };

  // Fetch filtered options per machine when quality or machines change
  useEffect(() => {
    const machineIds = formData.settings.map(s => s.machine).filter(Boolean);
    if (machineIds.length === 0) return;

    const fetches = machineIds.map(machineId => {
      const params = { type: 'process' };
      if (formData.quality.nozzle) params.nozzleSize = nozzleToDot(formData.quality.nozzle);
      if (formData.quality.layerHeight) params.layerHeight = formData.quality.layerHeight;
      const modelName = getMachineModelName(machineId);
      if (modelName) params.printerModel = modelName;

      return api.get('/auth/slicer-settings/base', { params })
        .then(res => ({
          machineId,
          options: (res.data?.data || []).map(item => ({ value: item._id, label: item.name }))
        }))
        .catch(() => ({ machineId, options: [] }));
    });

    Promise.all(fetches).then(results => {
      const newOpts = {};
      results.forEach(({ machineId, options }) => { newOpts[machineId] = options; });
      setFilteredOptions(newOpts);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-filter when quality/machine selection changes
  }, [formData.quality.nozzle, formData.quality.layerHeight, formData.settings.map(s => s.machine).join(','), slicerMachines, machineModels]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Clean up empty settings
      const { thumbnail: _Thumbnail, ...rest } = formData;
      const cleanedData = {
        ...rest,
        settings: formData.settings.filter(s => s.machine)
      };

      if (thumbnailFile) {
        const fd = new FormData();
        fd.append('thumbnail', thumbnailFile);
        Object.entries(cleanedData).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            fd.append(key, JSON.stringify(value));
          } else if (value !== undefined && value !== null) {
            fd.append(key, String(value));
          }
        });
        await onSave(fd);
      } else {
        await onSave(cleanedData);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save quality setting');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleI18nChange = (field, lang, value) => {
    setFormData({
      ...formData,
      [field]: { ...formData[field], [lang]: value }
    });
  };

  const handleQualityChange = (field, value) => {
    setFormData({
      ...formData,
      quality: {
        ...formData.quality,
        [field]: field === 'multiplier' ? parseFloat(value) || 0 : value
      }
    });
  };

  const handleSettingChange = (index, field, value) => {
    const newSettings = [...formData.settings];
    newSettings[index] = {
      ...newSettings[index],
      [field]: field === 'override' ? JSON.stringify(value) : value
    };
    setFormData({ ...formData, settings: newSettings });
  };

  const addSetting = () => {
    setFormData({
      ...formData,
      settings: [
        ...formData.settings,
        {
          machine: '',
          inherits: null,
          override: '{}'
        }
      ]
    });
  };

  const removeSetting = (index) => {
    const newSettings = formData.settings.filter((_, i) => i !== index);
    setFormData({ ...formData, settings: newSettings });
  };

  const createLoadOptions = (machineId) => async (inputValue) => {
    try {
      const params = { q: inputValue, type: 'process' };
      if (formData.quality.nozzle) params.nozzleSize = nozzleToDot(formData.quality.nozzle);
      if (formData.quality.layerHeight) params.layerHeight = formData.quality.layerHeight;
      const modelName = getMachineModelName(machineId);
      if (modelName) params.printerModel = modelName;
      const res = await api.get('/auth/slicer-settings/base', { params });
      return (res.data?.data || []).map(item => ({ value: item._id, label: item.name }));
    } catch (err) {
      console.error('Failed to fetch system settings:', err);
      return [];
    }
  };

  const autoLoadConfigs = async () => {
    if (machineModels.length === 0) return;
    setAutoLoading(true);
    try {
      // Build full settings list: keep existing, add missing machines
      const existingMachineIds = new Set(formData.settings.map(s => s.machine).filter(Boolean));
      const newSettings = [...formData.settings];
      machineModels.forEach(model => {
        if (!existingMachineIds.has(model._id)) {
          newSettings.push({ machine: model._id, inherits: null, override: '{}' });
        }
      });

      // Fetch first available option for every machine in parallel
      const fetches = newSettings.map((setting, idx) => {
        if (!setting.machine) return Promise.resolve({ settingIdx: idx, options: [] });
        const params = { type: 'process' };
        if (formData.quality.nozzle) params.nozzleSize = nozzleToDot(formData.quality.nozzle);
        if (formData.quality.layerHeight) params.layerHeight = formData.quality.layerHeight;
        const modelName = getMachineModelName(setting.machine);
        if (modelName) params.printerModel = modelName;
        return api.get('/auth/slicer-settings/base', { params })
          .then(res => ({
            settingIdx: idx,
            options: (res.data?.data || []).map(item => ({ value: item._id, label: item.name }))
          }))
          .catch(() => ({ settingIdx: idx, options: [] }));
      });

      const results = await Promise.all(fetches);

      // Pre-select first option (only if not already set)
      results.forEach(({ settingIdx, options }) => {
        if (options.length === 0) return;
        if (!newSettings[settingIdx].inherits) {
          newSettings[settingIdx] = { ...newSettings[settingIdx], inherits: options[0].value };
        }
      });

      setFormData(prev => ({ ...prev, settings: newSettings }));
    } catch (err) {
      console.error('Failed to auto-load configs:', err);
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {process ? 'Edit Quality Setting' : 'Add Quality Setting'}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Quality Settings */}
            <div className="space-y-4 border-t border-slate-200 pt-6">
              <h3 className="text-lg font-semibold text-slate-900">Quality Settings</h3>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="nozzle" className="block text-sm font-medium text-slate-700 mb-2">
                    Nozzle Size *
                  </label>
                  <select
                    id="nozzle"
                    value={formData.quality.nozzle}
                    onChange={(e) => handleQualityChange('nozzle', e.target.value)}
                    required
                    disabled={loading}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                  >
                    {NOZZLE_SIZES.map(size => (
                      <option key={size} value={size}>{size} mm</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="layerHeight" className="block text-sm font-medium text-slate-700 mb-2">
                    Layer Height *
                  </label>
                  <select
                    id="layerHeight"
                    value={formData.quality.layerHeight}
                    onChange={(e) => handleQualityChange('layerHeight', e.target.value)}
                    required
                    disabled={loading}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                  >
                    {LAYER_HEIGHTS.map(height => (
                      <option key={height} value={height}>{height}mm</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="multiplier" className="block text-sm font-medium text-slate-700 mb-2">
                    Price Multiplier *
                  </label>
                  <input
                    type="number"
                    id="multiplier"
                    value={formData.quality.multiplier}
                    onChange={(e) => handleQualityChange('multiplier', e.target.value)}
                    required
                    disabled={loading}
                    min={0}
                    step={0.1}
                    placeholder="1.0"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="space-y-4 border-t border-slate-200 pt-6">
              <h3 className="text-lg font-semibold text-slate-900">Profile Information</h3>

              <I18nField
                label="Profile Name"
                value={formData.name}
                onChange={(lang, val) => handleI18nChange('name', lang, val)}
                languages={supportedLanguages}
                required
                disabled={loading}
                maxLength={200}
              />

              <I18nField
                label="Description"
                value={formData.description}
                onChange={(lang, val) => handleI18nChange('description', lang, val)}
                languages={supportedLanguages}
                type="textarea"
                disabled={loading}
                rows={3}
                maxLength={500}
              />

              {/* Thumbnail */}
              <ThumbnailUpload
                currentUrl={formData.thumbnail?.url}
                file={thumbnailFile}
                onChange={setThumbnailFile}
                disabled={loading}
              />

            </div>

            {/* Machine-Specific Settings */}
            <div className="space-y-3 border-t border-slate-200 pt-6">
              <label className="block text-sm font-medium text-slate-700">
                Machine-Specific Settings
              </label>
              <p className="text-xs text-slate-500">
                Configure process settings for different machines with inheritance and overrides
              </p>

              {formData.settings.map((setting, index) => {
                let overrideData = {};
                try {
                  overrideData = setting.override ? JSON.parse(setting.override) : {};
                } catch (e) {
                  console.error(`Invalid JSON for setting ${index} override:`, e);
                }
                const hasOverrides = Object.keys(overrideData).length > 0;
                const isOverrideExpanded = expandedOverrides[index] || false;

                return (
                  <div key={index} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    {/* Machine + Inherits on same row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Machine model *
                        </label>
                        <select
                          value={setting.machine}
                          onChange={(e) => handleSettingChange(index, 'machine', e.target.value)}
                          disabled={loading}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white text-sm"
                        >
                          <option value="">Select machine model...</option>
                          {machineModels.map(m => (
                            <option key={m._id} value={m._id}>
                              {m.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Inherits from
                        </label>
                        <AsyncSelect
                          cacheOptions
                          defaultOptions={filteredOptions[setting.machine] || systemSettingsOptions}
                          isClearable
                          loadOptions={createLoadOptions(setting.machine)}
                          value={
                            setting.inherits
                              ? (filteredOptions[setting.machine] || systemSettingsOptions).find(opt => opt.value === setting.inherits)
                                || { value: setting.inherits, label: setting.inherits }
                              : null
                          }
                          onChange={(newValue) => {
                            handleSettingChange(index, 'inherits', newValue?.value || null);
                          }}
                          placeholder="Search by name..."
                          isDisabled={loading}
                          className="text-xs"
                          key={`${index}-${setting.machine}-${formData.quality.nozzle}-${formData.quality.layerHeight}`}
                        />
                      </div>
                    </div>

                    {/* Override accordion */}
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => setExpandedOverrides(prev => ({ ...prev, [index]: !prev[index] }))}
                        className="inline-flex items-center text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors"
                      >
                        {isOverrideExpanded
                          ? <ChevronDownIcon className="h-3.5 w-3.5 mr-1" />
                          : <ChevronRightIcon className="h-3.5 w-3.5 mr-1" />
                        }
                        Overrides{hasOverrides && <span className="ml-1 text-blue-600">({Object.keys(overrideData).length})</span>}
                      </button>
                      {isOverrideExpanded && (
                        <div className="mt-2">
                          <JSONEditor
                            data={overrideData}
                            onChange={(newSettings) => handleSettingChange(index, 'override', newSettings)}
                            disabled={loading}
                            minHeight={150}
                          />
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeSetting(index)}
                      disabled={loading}
                      className="mt-2 inline-flex items-center px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Remove
                    </button>
                  </div>
                );
              })}

              {(() => {
                const allConfigured = machineModels.length > 0 && machineModels.every(m => formData.settings.some(s => s.machine === m._id));
                const addDisabled = loading || autoLoading || allConfigured;
                const autoDisabled = loading || autoLoading || allConfigured;
                return (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={addSetting}
                      disabled={addDisabled}
                      className="inline-flex items-center px-4 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Machine Setting
                    </button>
                    <button
                      type="button"
                      onClick={autoLoadConfigs}
                      disabled={autoDisabled}
                      className="inline-flex items-center px-4 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      <BoltIcon className="h-4 w-4 mr-1" />
                      {autoLoading ? 'Loading...' : 'Auto-load all machine configs'}
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Quality-Level Override */}
            {(() => {
              let qualityOverrideData = {};
              try {
                qualityOverrideData = formData.override ? JSON.parse(formData.override) : {};
              } catch (e) {
                console.error('Invalid JSON for quality override:', e);
              }
              const hasQualityOverrides = Object.keys(qualityOverrideData).length > 0;
              const isQualityOverrideExpanded = expandedOverrides['quality'] || false;

              return (
                <div className="border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    onClick={() => setExpandedOverrides(prev => ({ ...prev, quality: !prev.quality }))}
                    className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
                  >
                    {isQualityOverrideExpanded
                      ? <ChevronDownIcon className="h-4 w-4 mr-1" />
                      : <ChevronRightIcon className="h-4 w-4 mr-1" />
                    }
                    Quality-Level Overrides{hasQualityOverrides && <span className="ml-1 text-blue-600">({Object.keys(qualityOverrideData).length})</span>}
                  </button>
                  <p className="text-xs text-slate-500 mt-1">
                    Slicer settings applied at the quality level, before machine-specific overrides
                  </p>
                  {isQualityOverrideExpanded && (
                    <div className="mt-2">
                      <JSONEditor
                        data={qualityOverrideData}
                        onChange={(newSettings) => setFormData(prev => ({ ...prev, override: JSON.stringify(newSettings) }))}
                        disabled={loading}
                        minHeight={150}
                      />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-2">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={loading}
                rows={2}
                maxLength={500}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 resize-none"
              />
            </div>

            {/* Active and ShowInSite Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="showInSite"
                    checked={formData.showInSite}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Show in Site</span>
                </label>
                <p className="mt-1 text-xs text-slate-500 ml-6">
                  Display this process setting in public listings
                </p>
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Active</span>
                </label>
                <p className="mt-1 text-xs text-slate-500 ml-6">
                  Enable this process setting for use
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
